import api from '../axios.config';

/**
 * Calendly and Cal.com, per organisation.
 *
 * The signing secret is write-only: it goes in and is never sent back. Nobody
 * needs to read a key they already pasted, and returning one would put it in a
 * response body, a browser and anybody's network tab.
 */

export type BookingProvider = 'CALENDLY' | 'CAL_COM';

export interface BookingIntegration {
  provider: BookingProvider;
  configured: boolean;
  enabled: boolean;
  /** Whether a secret is stored — never the secret itself. */
  hasSecret: boolean;
  /** Whether an API token is stored — also never the token itself. */
  hasToken: boolean;
  /** Whose account the token belongs to, once tested. */
  account: string | null;
  /** When the subscription was last confirmed at the vendor. */
  verifiedAt: string | null;
  externalRef: string | null;
  /**
   * The absolute URL to paste into the vendor.
   *
   * Built by the server, which is the only side that knows the API host. This
   * screen is served from the app host, and the two differ per brand — deriving
   * one from the other in the browser produced a real-but-wrong host the moment
   * a second brand's domain was pointed at this bundle.
   */
  webhookUrl: string;
}

export const PROVIDER_META: Record<BookingProvider, { label: string; help: string; header: string }> = {
  CALENDLY: {
    label: 'Calendly',
    help: 'Subscribe to invitee.created and invitee.canceled. Webhooks need a Standard plan or above.',
    header: 'Calendly-Webhook-Signature',
  },
  CAL_COM: {
    label: 'Cal.com',
    help: 'Subscribe to BOOKING_CREATED, BOOKING_RESCHEDULED and BOOKING_CANCELLED. Available on every plan.',
    header: 'X-Cal-Signature-256',
  },
};

const unwrap = <T,>(res: any): T | null =>
  res?.data?.success && res.data.data !== undefined ? (res.data.data as T) : null;

export const bookingIntegrationsService = {
  async list(): Promise<BookingIntegration[]> {
    try {
      return unwrap<BookingIntegration[]>(await api.get('/v1/bookings/integrations')) ?? [];
    } catch {
      return [];
    }
  },

  /**
   * Save one provider.
   *
   * Sending a blank secret leaves the stored one alone — the screen cannot show
   * what is stored, so it sends blank whenever nobody retyped it, and treating
   * that as "clear the key" would break a working integration the next time
   * somebody toggled it.
   */
  async save(
    provider: BookingProvider,
    patch: { signingSecret?: string; apiToken?: string; enabled?: boolean; externalRef?: string },
  ): Promise<{ ok: true; data: BookingIntegration } | { ok: false; error: string }> {
    try {
      const res = await api.put(`/v1/bookings/integrations/${provider}`, patch);
      const data = unwrap<BookingIntegration>(res);
      return data
        ? { ok: true, data: data as BookingIntegration }
        : { ok: false, error: res?.data?.message || 'Could not save.' };
    } catch (e: any) {
      return {
        ok: false,
        error: e?.response?.data?.message || e?.response?.data?.error || 'Could not save.',
      };
    }
  },
};

/** A Test Connection result: never an error status, always a reason. */
export interface ConnectionResult {
  ok: boolean;
  reason: string | null;
  account: string | null;
  webhookUrl?: string;
}

export interface BookingEventType {
  name: string;
  url: string;
  minutes: string;
}

export const bookingConnectService = {
  /**
   * Does this token work, and whose is it?
   *
   * The account name is the answer that matters: a token pasted from the wrong
   * login authenticates perfectly and would subscribe somebody else's diary.
   */
  async test(provider: BookingProvider, apiToken?: string): Promise<ConnectionResult> {
    try {
      const res = await api.post(`/v1/bookings/integrations/${provider}/test`, { apiToken });
      return (unwrap<ConnectionResult>(res)) ?? { ok: false, reason: 'No answer.', account: null };
    } catch (e: any) {
      return {
        ok: false,
        reason: e?.response?.data?.message || 'Could not reach the server.',
        account: null,
      };
    }
  },

  /**
   * Register the webhook at the vendor.
   *
   * This is what the token is for: the server generates the signing secret and
   * registers the URL itself, so neither is ever typed into two dashboards.
   */
  async connect(provider: BookingProvider, apiToken?: string): Promise<ConnectionResult> {
    try {
      const res = await api.post(`/v1/bookings/integrations/${provider}/connect`, { apiToken });
      return (unwrap<ConnectionResult>(res)) ?? { ok: false, reason: 'No answer.', account: null };
    } catch (e: any) {
      return {
        ok: false,
        reason: e?.response?.data?.message || 'Could not reach the server.',
        account: null,
      };
    }
  },

  /** What this account offers to book. Empty is an ordinary "not connected yet". */
  async eventTypes(provider: BookingProvider): Promise<BookingEventType[]> {
    try {
      return unwrap<BookingEventType[]>(
        await api.get(`/v1/bookings/integrations/${provider}/event-types`)) ?? [];
    } catch {
      return [];
    }
  },
};
